import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import productService from '../../../services/productService';
import { useCurrency } from '../../../hooks/useCurrency';

const PriceHistoryList = ({ variantId }) => {
  const { t } = useTranslation();
  const [priceHistory, setPriceHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (variantId) fetchPriceHistory();
  }, [variantId]);

  const fetchPriceHistory = async () => {
    setLoading(true);
    try {
      const data = await productService.getVariantPriceHistory(variantId);
      setPriceHistory(data || []);
    } catch (error) {
      toast.error(t('common.loadError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const { format: formatPrice } = useCurrency();

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('fr-FR', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getPriceChange = (current, previous) => {
    if (!previous) return null;
    const change = current - previous;
    const percentChange = ((change / previous) * 100).toFixed(2);
    return {
      amount: change,
      percent: percentChange,
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'same',
    };
  };

  const renderPriceChangeIcon = (change) => {
    if (!change) return null;
    if (change.direction === 'up') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
          <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />
          <Typography variant="body2" color="error">
            +{formatPrice(change.amount)} ({change.percent}%)
          </Typography>
        </Box>
      );
    } else if (change.direction === 'down') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
          <TrendingDownIcon fontSize="small" sx={{ mr: 0.5 }} />
          <Typography variant="body2" color="success">
            {formatPrice(change.amount)} ({change.percent}%)
          </Typography>
        </Box>
      );
    } else {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
          <RemoveIcon fontSize="small" sx={{ mr: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            {t('priceHistory.noChange')}
          </Typography>
        </Box>
      );
    }
  };

  if (!variantId) {
    return <Alert severity="info">{t('priceHistory.selectVariant')}</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (priceHistory.length === 0) {
    return <Alert severity="info">{t('priceHistory.noHistory')}</Alert>;
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('priceHistory.history', { count: priceHistory.length })}
        </Typography>

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.date')}</TableCell>
                <TableCell>{t('priceHistory.oldPrice')}</TableCell>
                <TableCell>{t('priceHistory.newPrice')}</TableCell>
                <TableCell>{t('priceHistory.change')}</TableCell>
                <TableCell>{t('priceHistory.changedBy')}</TableCell>
                <TableCell>{t('priceHistory.reason')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {priceHistory.map((record, index) => {
                const previousPrice = index < priceHistory.length - 1
                  ? priceHistory[index + 1].newPrice
                  : record.oldPrice;
                const change = getPriceChange(record.newPrice, previousPrice);

                return (
                  <TableRow key={record.id}>
                    <TableCell>
                      <Typography variant="body2">{formatDate(record.changedAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {record.oldPrice ? formatPrice(record.oldPrice) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPrice(record.newPrice)}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderPriceChangeIcon(change)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {record.changedByUserName || 'System'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {record.reason ? (
                        <Chip label={record.reason} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default PriceHistoryList;
